import { useSyncExternalStore } from 'react'
import { api, meekijkStopBijWeggaan } from '../../lib/api'
import { maakDeler, type Deler, type DelerFase, type MeekijkServer } from '../../lib/meekijken'

/* ===========================================================================
 *  Het delen dat nu loopt, voor de hele app
 *
 *  Niet in het scherm /meekijken/<id> zelf: wie zijn scherm deelt en in IT
 *  naar een ander scherm klikt, deelt gewoon door, en de balk "Je deelt je
 *  scherm met ..." staat dan nog steeds bovenaan (de Shell toont hem). Pas
 *  bij Stoppen, als de browser het delen beëindigt, of als het tabblad
 *  dichtgaat, houdt het op.
 *
 *  startDelen() roept getDisplayMedia als eerste aan, zonder iets ervoor
 *  af te wachten: zo valt hij nog binnen de klik op Accepteren (in de
 *  melding of op het scherm), en anders weigert de browser.
 * =========================================================================== */

export const server: MeekijkServer = {
  stand: (id) => api.meekijkStand(id),
  signaal: (id, sdp) => api.meekijkSignaal(id, sdp),
  antwoord: (id, akkoord) => api.meekijkAntwoord(id, akkoord),
  stop: (id) => api.meekijkStop(id),
}

export interface Delen {
  id: string
  /** Met wie: de ontwikkelaar die het vroeg. */
  naam: string
  fase: DelerFase
  tekst: string | null
}

let nu: Delen | null = null
let deler: Deler | null = null
const luisteraars = new Set<() => void>()

function zet(d: Delen | null) {
  nu = d
  for (const l of luisteraars) l()
}

export function useDelen(): Delen | null {
  return useSyncExternalStore((l) => { luisteraars.add(l); return () => { luisteraars.delete(l) } }, () => nu)
}

/** Accepteren: het scherm kiezen (of het al gekozen scherm), akkoord, en het aanbod. */
export async function startDelen(id: string, naam: string, stroom?: MediaStream): Promise<boolean> {
  if (deler && nu && nu.fase !== 'gestopt' && nu.fase !== 'mislukt') {
    if (nu.id === id) return true
    /* Eén tegelijk. De oude houdt op; niet erop wachten (zie boven). */
    void deler.stop('zelf')
  }
  const deze = maakDeler({
    id,
    server,
    opFase: (fase, tekst) => {
      if (deler !== deze) return
      zet({ id, naam, fase, tekst: tekst ?? null })
    },
  })
  deler = deze
  return await deze.start(stroom)
}

export async function stopDelen(): Promise<void> {
  const d = deler
  if (!d) return
  await d.stop('zelf')
}

/** Na een mislukte poging of na stoppen: de balk weg. */
export function vergeetDelen() {
  if (nu && (nu.fase === 'gestopt' || nu.fase === 'mislukt')) { deler = null; zet(null) }
}

/* Tabblad dicht of ververst: de tracks stoppen vanzelf, de server hoort het
   via een vraag die de pagina overleeft. */
window.addEventListener('pagehide', () => {
  if (!deler || !nu || nu.fase === 'gestopt' || nu.fase === 'mislukt') return
  const id = nu.id
  void deler.stop('ander')
  meekijkStopBijWeggaan(id)
})
